import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { PropertySearchListModel } from 'app/modules/main/api/main-clients.api';
import { useInstance } from 'react-ioc';
import { SearchStore } from './SearchStore';

interface GoogleMapsProps {
    items: PropertySearchListModel[];
    height?: string;
}

declare global {
    interface Window {
        google: any;
        initMap: () => void;
    }
}

const GoogleMaps = React.memo(function GoogleMaps({ items, height = "400px" }: GoogleMapsProps) {
    const mapRef = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<any>(null);
    const [markers, setMarkers] = useState<any[]>([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const searchStore = useInstance(SearchStore);

    // Memoize valid items to prevent unnecessary re-renders
    const validItems = useMemo(() =>
        items.filter(item => item.latitude && item.longitude),
        [items]
    );

    // Check if Google Maps is loaded
    useEffect(() => {
        const checkGoogleMaps = () => {
            if (window.google && window.google.maps) {
                setIsLoaded(true);
            } else {
                setTimeout(checkGoogleMaps, 100);
            }
        };
        checkGoogleMaps();
    }, []);

    // Initialize map when Google Maps is loaded (only once)
    useEffect(() => {
        if (!isLoaded || !mapRef.current || map) return;

        if (validItems.length === 0) {
            // Default to Los Angeles if no valid coordinates
            const defaultCenter = { lat: 34.0522, lng: -118.2437 };
            const mapInstance = new window.google.maps.Map(mapRef.current, {
                zoom: 10,
                center: defaultCenter,
                mapTypeId: window.google.maps.MapTypeId.ROADMAP,
                styles: [
                    {
                        featureType: "poi",
                        elementType: "labels",
                        stylers: [{ visibility: "off" }]
                    }
                ]
            });
            setMap(mapInstance);
            return;
        }

        // Calculate bounds to fit all properties
        const bounds = new window.google.maps.LatLngBounds();
        validItems.forEach(item => {
            bounds.extend(new window.google.maps.LatLng(item.latitude!, item.longitude!));
        });

        // Create map
        const mapInstance = new window.google.maps.Map(mapRef.current, {
            zoom: 10,
            center: bounds.getCenter(),
            mapTypeId: window.google.maps.MapTypeId.ROADMAP,
            styles: [
                {
                    featureType: "poi",
                    elementType: "labels",
                    stylers: [{ visibility: "off" }]
                }
            ]
        });

        // Fit map to bounds
        mapInstance.fitBounds(bounds);

        // Add padding to bounds
        const listener = window.google.maps.event.addListener(mapInstance, 'idle', () => {
            if (validItems.length > 1) {
                mapInstance.setZoom(Math.min(mapInstance.getZoom()!, 15));
            }
            window.google.maps.event.removeListener(listener);
        });

        setMap(mapInstance);
    }, [isLoaded, validItems, map]);

    // Add markers when map is ready
    useEffect(() => {
        if (!map || !isLoaded) return;

        // Clear existing markers
        markers.forEach(marker => marker.setMap(null));
        const newMarkers: any[] = [];

        // Add markers for each property
        validItems.forEach((item, index) => {

            const marker = new window.google.maps.Marker({
                position: { lat: item.latitude, lng: item.longitude },
                map: map,
                title: item.name || `Property ${index + 1}`,
                animation: window.google.maps.Animation.DROP,
                icon: {
                    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                        <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="16" cy="16" r="12" fill="#1976d2" stroke="#fff" stroke-width="2"/>
                            <text x="16" y="20" text-anchor="middle" fill="white" font-family="Arial" font-size="12" font-weight="bold">${index + 1}</text>
                        </svg>
                    `),
                    scaledSize: new window.google.maps.Size(32, 32),
                    anchor: new window.google.maps.Point(16, 16)
                }
            });

            // Add click listener to marker
            marker.addListener('click', () => {
                searchStore.openPropertyPopup(item);
            });

            newMarkers.push(marker);
        });

        setMarkers(newMarkers);
    }, [map, validItems, isLoaded]);

    if (!isLoaded) {
        return (
            <Box
                sx={{
                    height,
                    backgroundColor: "#f5f5f5",
                    border: "2px dashed #ccc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 1
                }}
            >
                <Typography variant="body1" color="text.secondary">
                    Loading Google Maps...
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            ref={mapRef}
            sx={{
                height,
                width: "100%",
                borderRadius: 1,
                overflow: "hidden"
            }}
        />
    );
});

export default GoogleMaps;
